import { AppContext } from 'context/AppContext';
import { useState, useRef, useCallback, useContext } from 'react';

export const usePreciseDragDropGant = ({
  chartRef,
  minDate,
  maxDate,
  totalChartWidth,
  isSnapping = false,       // Jeśli chcesz używać przycisku magnesu
  snapMinutes = 60,         // Do ilu minut przyciągać, gdy magnes jest włączony (np. 60 lub 15)
  useGrabOffset = false,    // false = czas dokładnie pod kursorem myszy, true = czas lewej krawędzi klocka
  onDropFreeSpace,          // Callback dla API: upuszczenie w wolne miejsce
  onDropOnStage,            // Callback dla API: upuszczenie na inny klocek
}) => {
  const [draggedStage, setDraggedStage] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const { procesory } = useContext(AppContext);
  
  // Przechowujemy miejsce złapania klocka (odległość kursora od lewej krawędzi klocka)
  const grabOffsetPxRef = useRef(0);

  // Formatowanie dla API (MySQL: YYYY-MM-DD HH:mm)
  const formatDateForApi = (dateObj) => {
    if (!dateObj) return null;
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Formatowanie dla podglądu (np. "Pt 23.10 | 14:37")
  const formatDateForTooltip = (dateObj) => {
    if (!dateObj) return '';
    const days = ['Nd', 'Pn', 'Wt', 'Śr', 'Czw', 'Pt', 'So'];
    const dayName = days[dateObj.getDay()];
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    return `${dayName} ${day}.${month} | ${hours}:${minutes}`;
  };

  // Wyliczanie dokładnej daty co do minuty na podstawie pozycji X kursora
  const calculateTimeFromClientX = useCallback(
    (clientX) => {
      if (!chartRef.current || !minDate || !maxDate || !totalChartWidth) {
        return null;
      }

      const rect = chartRef.current.getBoundingClientRect();
      const rawXPx = clientX - rect.left - (useGrabOffset ? grabOffsetPxRef.current : 0);
      const clampedXPx = Math.max(0, Math.min(rawXPx, totalChartWidth));

      const totalTimeSpanMs = maxDate.getTime() - minDate.getTime();
      const timeOffsetMs = (clampedXPx / totalChartWidth) * totalTimeSpanMs;
      const rawTimestampMs = minDate.getTime() + timeOffsetMs;

      const oneMinuteMs = 60 * 1000;
      let targetDate = new Date(Math.round(rawTimestampMs / oneMinuteMs) * oneMinuteMs);

      if (isSnapping && snapMinutes > 1) {
        const snapMs = snapMinutes * 60 * 1000;
        targetDate = new Date(Math.round(targetDate.getTime() / snapMs) * snapMs);
      }

      const finalOffsetMs = targetDate.getTime() - minDate.getTime();
      const snappedXPx = (finalOffsetMs / totalTimeSpanMs) * totalChartWidth;

      return {
        dateObj: targetDate,
        apiDate: formatDateForApi(targetDate),
        displayDate: formatDateForTooltip(targetDate),
        xPx: snappedXPx,
        cursorXPx: clientX - rect.left,
        cursorYPx: rect.top,
      };
    },
    [chartRef, minDate, maxDate, totalChartWidth, isSnapping, snapMinutes, useGrabOffset]
  );

  // 1. START PRZECIĄGANIA KLOCKA
  const handleDragStart = (stage, e) => {
    sessionStorage.setItem("proces_nazwa_id", stage.proces_nazwa_id);
    e.dataTransfer.setData("application/json", JSON.stringify(stage));

    const barRect = e.currentTarget.getBoundingClientRect();
    grabOffsetPxRef.current = e.clientX - barRect.left;

    setDraggedStage(stage);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(stage.global_id));
  };

  // 2. NAJECHANIE NA INNY KLOCEK
  const handleDragOverStage = (targetStage, processorId, e) => {
    if (!draggedStage) return;

    const dragProcesId = parseInt(sessionStorage.getItem("proces_nazwa_id") || draggedStage.proces_nazwa_id);
    const targetProcesId = parseInt(targetStage.proces_nazwa_id);
    const dragProcesorId = parseInt(draggedStage.procesor_id);
    const targetProcesorId = parseInt(processorId);
    const dragTypGrupy = parseInt(draggedStage.typ_grupy);

    // ==========================================
    // WERYFIKACJA REGUŁ BIZNESOWYCH (NA KLOCEK)
    // ==========================================
    if (isNaN(dragProcesId) || isNaN(targetProcesId) || dragProcesId != targetProcesId) {
      return; 
    }

    const isChangingProcessor = dragProcesorId != targetProcesorId;

    if (isChangingProcessor) {
      if (dragProcesId == 6) return; // Oprawa
      if (dragTypGrupy != 2) return; // Przerwa
    }
// ==========================================
    // NOWOŚĆ: Blokada przerwy samej na siebie
    // ==========================================
    if (targetStage.global_id === draggedStage.global_id) {
      if (dragTypGrupy === 1) {
        e.stopPropagation(); // Zabijamy zdarzenie, żeby nie dotarło do pustego wiersza
        setDragPreview(null);
        return; // Brak e.preventDefault() -> całkowita blokada upuszczenia
      }
      // Jeśli to normalna grupa (typ_grupy 2), pozwalamy zdarzeniu przejść wyżej
      // do handleDragOverRow, aby narysowała się zielona linia wolnej przestrzeni (przesunięcie klocka)
      return;
    }
    // ==========================================
    // REGUŁY SPEŁNIONE - ZEZWALAMY NA DROP
    // ==========================================
    e.preventDefault(); 
    e.dataTransfer.dropEffect = 'move';

    if (targetStage.global_id === draggedStage.global_id) {
      return;
    }

    e.stopPropagation();

    const timeData = calculateTimeFromClientX(e.clientX);
    if (!timeData) return;

    const chartRect = chartRef.current.getBoundingClientRect();
    const relativeY = e.clientY - chartRect.top;

    setDragPreview((prev) => {
      if (
        prev?.mode === 'ON_STAGE' &&
        prev?.targetStage?.global_id === targetStage.global_id &&
        prev?.apiDate === timeData.apiDate &&
        Math.abs(prev.yPx - relativeY) < 4
      ) {
        return prev;
      }
      return {
        mode: 'ON_STAGE', 
        targetProcessorId: Number(processorId),
        targetStage,
        ...timeData,
        yPx: relativeY,
      };
    });
  };

  // 3. NAJECHANIE NA WOLNĄ PRZESTRZEŃ PROCESORA
  const handleDragOverRow = (processorId, e) => {
    if (!draggedStage) return;

    // Pobieramy ID docelowego procesu z Twojej listy procesorów w kontekście
    const targetProcesorObj = procesory.find(x => parseInt(x.id) === parseInt(processorId));
    const targetProcesId = targetProcesorObj ? parseInt(targetProcesorObj.grupa) : NaN;

    const dragProcesId = parseInt(sessionStorage.getItem("proces_nazwa_id") || draggedStage.proces_nazwa_id);
    const dragProcesorId = parseInt(draggedStage.procesor_id);
    const targetProcesorId = parseInt(processorId);
    const dragTypGrupy = parseInt(draggedStage.typ_grupy);

    // ==========================================
    // WERYFIKACJA REGUŁ BIZNESOWYCH (PUSTY WIERSZ)
    // ==========================================
    
    // 1. Zgodność procesu
    if (isNaN(dragProcesId) || isNaN(targetProcesId) || dragProcesId !== targetProcesId) {
      return; // Blokujemy upuszczenie (kursor zakazu)
    }

    // 2. Zmiana maszyny
    const isChangingProcessor = dragProcesorId !== targetProcesorId;

    if (isChangingProcessor) {
      if (dragProcesId === 6) return; // Zakaz przenoszenia oprawy
      if (dragTypGrupy !== 2) return; // Zakaz przenoszenia przerw
    }

    // ==========================================
    // REGUŁY SPEŁNIONE - ZEZWALAMY NA DROP
    // ==========================================
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const timeData = calculateTimeFromClientX(e.clientX);
    if (!timeData) return;

    const chartRect = chartRef.current.getBoundingClientRect();
    const relativeY = e.clientY - chartRect.top;

    setDragPreview((prev) => {
      if (
        prev?.mode === 'FREE_SPACE' &&
        prev?.targetProcessorId === Number(processorId) &&
        prev?.apiDate === timeData.apiDate &&
        Math.abs(prev.yPx - relativeY) < 4
      ) {
        return prev;
      }
      return {
        mode: 'FREE_SPACE', 
        targetProcessorId: Number(processorId),
        targetStage: null,
        ...timeData,
        yPx: relativeY,
      };
    });
  };

  // 4. UPUSZCZENIE NA INNY KLOCEK
  const handleDropOnStage = (targetStage, processorId, e) => {
    e.preventDefault();
    if (!draggedStage) return;

    if (targetStage.global_id === draggedStage.global_id) {
      return;
    }

    e.stopPropagation();
    const timeData = calculateTimeFromClientX(e.clientX);

    if (timeData && onDropOnStage) {
      onDropOnStage({
        draggedStage,
        targetStage,
        targetProcessorId: Number(processorId),
        dropDate: timeData.apiDate,     
        dropDateObj: timeData.dateObj,  
      });
    }

    setDraggedStage(null);
    setDragPreview(null);
  };

  // 5. UPUSZCZENIE W WOLNĄ PRZESTRZEŃ PROCESORA
  const handleDropOnRow = (processorId, e) => {
    e.preventDefault();
    if (!draggedStage) return;

    const timeData = calculateTimeFromClientX(e.clientX);

    if (timeData && onDropFreeSpace) {
      onDropFreeSpace({
        draggedStage,
        targetProcessorId: Number(processorId),
        dropDate: timeData.apiDate,     
        dropDateObj: timeData.dateObj,  
      });
    }

    setDraggedStage(null);
    setDragPreview(null);
  };

  // 6. ZAKOŃCZENIE PRZECIĄGANIA (np. klawisz ESC lub puszczenie poza wykresem)
  const handleDragEnd = () => {
    setDraggedStage(null);
    setDragPreview(null);
  };

  return {
    draggedStage,
    dragPreview,
    handleDragStart,
    handleDragOverStage,
    handleDragOverRow,
    handleDropOnStage,
    handleDropOnRow,
    handleDragEnd,
  };
};